import React from 'react';
import { motion } from 'framer-motion';
import { Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CandidateResult {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  // CV Metadata (optional)
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
}

interface CandidateCardProps {
  result: CandidateResult;
  keywords: string[];
  isSelected: boolean;
  onClick: () => void;
  index: number;
  isSaved?: boolean;
  onToggleSave?: (e: React.MouseEvent) => void;
}

const CandidateCard: React.FC<CandidateCardProps> = ({ 
  result, 
  keywords, 
  isSelected, 
  onClick, 
  index,
  isSaved = false,
  onToggleSave
}) => {
  const { t } = useTranslation('hire');
  
  // Format career status
  const formatCareerStatus = (status?: string) => {
    if (!status) return null;
    const statusMap: Record<string, string> = {
      'actively_seeking': t('results.careerStatusReady'),
      'curious': t('results.careerStatusCurious'),
      'invisible': t('results.careerStatusInvisible')
    };
    return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format locations (truncate if too long)
  const formatLocations = (locations?: string) => {
    if (!locations) return null;
    if (locations.length > 30) {
      return locations.substring(0, 30) + '...';
    }
    return locations;
  };

  const displayKeywords = keywords.slice(0, 8);

  return (
    <motion.div
      onClick={onClick}
      className={`
        relative bg-white/95 backdrop-blur-lg rounded-xl p-3 sm:p-4 cursor-pointer
        border-2 transition-all duration-100 shadow-md hover:shadow-xl
        ${isSelected 
          ? 'border-slate-400 bg-slate-50/50 shadow-xl' 
          : 'border-slate-300 hover:border-slate-400'
        }
      `}
      whileHover={{
        scale: 1.02,
        y: -2,
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15 }}
      layout
    >
      {onToggleSave && (
        <button
          type="button"
          onClick={onToggleSave}
          title={isSaved ? t('results.savedCandidate') : t('results.saveCandidate')}
          className={`absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 p-1 sm:p-1.5 rounded-lg transition-all touch-manipulation ${
            isSaved
              ? 'text-slate-700 bg-slate-100 hover:bg-slate-200'
              : 'text-slate-300 hover:text-slate-500 hover:bg-slate-50'
          }`}
        >
          <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-slate-700' : ''}`} />
        </button>
      )}

      {result.reach_out_id && (
        <div className={`mb-1.5 sm:mb-2 ${onToggleSave ? 'pr-10' : ''}`}>
          <span className="text-[10px] font-medium text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full border border-pink-200">
            {t('results.alreadyContacted')}
          </span>
        </div>
      )}

      
      {(result.career_status || result.base_locations || result.preferred_locations || result.role_preferences || result.current_role || result.current_level) && (
        <motion.div 
          className="space-y-1 text-[11px] sm:text-xs text-slate-600"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            delay: 0.7 + (index * 0.1),
            duration: 0.3
          }}
        >
          {result.career_status && (
            <div className="flex items-center gap-1.5">
              <span className="text-emerald-600">💼</span>
              <span className="font-medium text-slate-700">{t('results.careerStatus')}:</span>
              <span>{formatCareerStatus(result.career_status)}</span>
            </div>
          )}
          
          {result.base_locations && (
            <div className="flex items-center gap-1.5">
              <span className="text-blue-600">📍</span>
              <span className="font-medium text-slate-700">{t('results.baseLocation')}:</span>
              <span>{formatLocations(result.base_locations)}</span>
            </div>
          )}
          
          {result.preferred_locations && (
            <div className="flex items-center gap-1.5">
              <span className="text-cyan-600">🌍</span>
              <span className="font-medium text-slate-700">{t('results.preferredLocation')}:</span>
              <span>{formatLocations(result.preferred_locations)}</span>
            </div>
          )}
          
          {result.role_preferences && (
            <div className="flex items-start gap-1.5">
              <span className="text-purple-600 mt-0.5">🎯</span>
              <span className="font-medium text-slate-700">{t('results.rolePreferences')}:</span>
              <span>{formatLocations(result.role_preferences)}</span>
            </div>
          )}
          
          {result.current_role && (
            <div className="flex items-center gap-1.5">
              <span className="text-orange-600">👔</span>
              <span className="font-medium text-slate-700">{t('results.currentRole')}:</span>
              <span>{result.current_role}</span>
            </div>
          )}
          
          {result.current_level && (
            <div className="flex items-center gap-1.5">
              <span className="text-indigo-600">📊</span>
              <span className="font-medium text-slate-700">{t('results.currentLevel')}:</span>
              <span className="capitalize">{result.current_level}</span>
            </div>
          )}
        </motion.div>
      )}

      {/* Selection Indicator */}
      {isSelected && (
        <motion.div
          className="absolute inset-0 rounded-xl border-2 border-slate-500 pointer-events-none"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        />
      )}
    </motion.div>
  );
};

export default CandidateCard;
